import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { api, type DeckVersion } from '../lib/api';
import { useDeckStore } from '../store/deckStore';

function formatWhen(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export function VersionHistoryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const deckId = useDeckStore((s) => s.deckId);
  const restoreVersion = useDeckStore((s) => s.restoreVersion);
  const syncSave = useDeckStore((s) => s.syncSave);
  const [versions, setVersions] = useState<DeckVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!deckId) return;
    setLoading(true);
    setError(null);
    try {
      const { versions: list } = await api.listVersions(deckId);
      setVersions(list);
      setSelected(list[0]?.id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load history');
    } finally {
      setLoading(false);
    }
  }, [deckId]);

  const checkpoint = async () => {
    setBusy(true);
    setError(null);
    try {
      await syncSave({ force: true, saveVersion: true });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save checkpoint');
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await restoreVersion(selected);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore this version');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="version-title"
      slotProps={{
        transition: {
          onEnter: () => {
            void load();
          },
        },
      }}
    >
      <DialogTitle id="version-title" component="h2" variant="h6">
        Version history
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Snapshots are saved automatically every few minutes while you edit. Restore replaces the current
            deck (a snapshot of the current state is kept first).
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          {loading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
              <CircularProgress size={28} aria-label="Loading versions" />
            </Box>
          ) : versions.length === 0 ? (
            <Alert severity="info">No snapshots yet. Keep editing, or save a checkpoint now.</Alert>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 320, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {versions.map((v) => (
                <ListItemButton
                  key={v.id}
                  selected={selected === v.id}
                  onClick={() => setSelected(v.id)}
                >
                  <ListItemText
                    primary={v.title || 'Untitled'}
                    secondary={`${formatWhen(v.created_at)} · ${v.slide_count ?? '?'} slides${v.author_name ? ` · ${v.author_name}` : ''}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="neutral"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <HistoryOutlinedIcon />}
          disabled={busy || !deckId}
          onClick={() => void checkpoint()}
        >
          Save checkpoint
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" color="neutral" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="contained"
          disabled={busy || !selected}
          onClick={() => void restore()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );
}
